import { getCurrentUserIsAnonymous } from '@helpers/user';

const CARD_IMAGE_PLACEHOLDER_SRC =
  'https://cdn.brandfolder.io/27C9EC93/at/qaa5h5-4gv694-g5cyc8/icon-placeholder-brandguide.svg';

/**
 * Repaint the thumbnail on a server-rendered Brandguide card (see
 * `brandfolders/_brandfolder_card`), which React does not otherwise own.
 *
 * A saved card image keeps `url: nil` until the Filemover webhook lands, so the card
 * would render the placeholder for several seconds after a successful upload. The
 * uploader's signed url is readable straight away and bridges that gap.
 *
 * @param brandguideKey key of the Brandguide whose card should be repainted
 * @param url signed image url, or an empty string to restore the placeholder
 */
export const updateBrandguideCardImage = (brandguideKey: string, url: string): void => {
  const container = document
    .getElementById(`brandguide-org-card-actions-${brandguideKey}`)
    ?.closest('.m-brandfolder-card')
    ?.querySelector('.m-asset-thumb-container');
  if (!container) return;

  const thumb = document.createElement('div');

  if (url) {
    thumb.className = 'm-asset-thumb';
    const image = document.createElement('div');
    image.className = 'm-asset-image';
    image.dataset.private = 'true';
    // A double-quoted css url() can only be broken out of by a literal double quote
    image.style.backgroundImage = `url("${url.replace(/"/g, '%22')}")`;
    thumb.appendChild(image);
  } else {
    thumb.className = 'm-asset-thumb placeholder-card-image';
    const placeholder = document.createElement('img');
    placeholder.alt = '';
    placeholder.src = CARD_IMAGE_PLACEHOLDER_SRC;
    thumb.appendChild(placeholder);
  }

  container.innerHTML = '';
  container.appendChild(thumb);
};

const PENDING_CARD_IMAGES_KEY = 'brandguidePendingCardImages';
// Processing usually takes seconds; the backend's own enable_after grace is 5 minutes
const PENDING_CARD_IMAGE_TTL_MS = 5 * 60 * 1000;

interface PendingCardImage {
  savedAt: number;
  url: string;
}

const readPendingCardImages = (): Record<string, PendingCardImage> => {
  try {
    return JSON.parse(
      window.sessionStorage.getItem(PENDING_CARD_IMAGES_KEY) || '{}'
    ) as Record<string, PendingCardImage>;
  } catch {
    return {};
  }
};

const writePendingCardImages = (pending: Record<string, PendingCardImage>): void => {
  try {
    window.sessionStorage.setItem(PENDING_CARD_IMAGES_KEY, JSON.stringify(pending));
  } catch {
    // sessionStorage can be unavailable (private browsing, quota) and the preview is optional
  }
};

/**
 * Remember a card image the user just saved, so it can be shown back before the
 * Filemover webhook has written the url. Pass an empty url when the image was removed.
 */
export const setPendingCardImage = (brandguideKey: string, url: string): void => {
  const pending = readPendingCardImages();

  if (url) {
    pending[brandguideKey] = { savedAt: Date.now(), url };
  } else {
    delete pending[brandguideKey];
  }

  writePendingCardImages(pending);
};

/**
 * Pick the card image to display. The api reports an empty `card_image` until the
 * Filemover webhook lands, which would make a freshly saved upload look lost, so fall
 * back to the url held from that save. The saved url wins as soon as it exists.
 */
export const resolveCardImage = (brandguideKey: string, savedUrl: string): string => {
  const pending = readPendingCardImages();
  const entry = pending[brandguideKey];
  const isFresh = !!entry && Date.now() - entry.savedAt < PENDING_CARD_IMAGE_TTL_MS;

  if (savedUrl || !isFresh) {
    if (entry) {
      delete pending[brandguideKey];
      writePendingCardImages(pending);
    }
    return savedUrl;
  }

  return entry.url;
};

interface GenerateBrandguideUrlOptions {
  slug: string;
  cname?: string | null;
  organizationKey?: string | null;
  v2?: boolean | null;
  public?: boolean;
}

/**
 * Generate the correct Brandguide URL for either v1 or v2 Brandguides.
 * It requires the `BFG` window global being available.
 *
 * @param options GenerateBrandguideUrlOptions
 * @returns string
 */
export const generateBrandguideUrl = (options: GenerateBrandguideUrlOptions): string => {
  const { cname, organizationKey, slug, v2 } = options;

  if (v2) {
    const cnameParam = cname ? `&cname=${cname}` : '';
    const organizationKeyParam = organizationKey ? `&org_key=${organizationKey}` : '';
    return `${BFG.brandguideUrl}?redirect=${slug}${cnameParam}${organizationKeyParam}`;
  } else if (cname) {
    return `https://${cname}/${slug}`;
  }  else {
    return `${BFG.brandguideV1Url}/${slug}`;
  }
};

export const generatePublicV2BrandguideUrl = (options: GenerateBrandguideUrlOptions): string => {
  const { cname, slug } = options;

  if (cname) {
    return `https://${cname}`;
  }

  return `${BFG.brandguideUrl}/${slug}`;
};

/**
 * Generate a permission-sensitive Brandguide URL
 * If the Brandguide is public and the user is not authorized as a guest
 * (the Brandfolder/Collection is public and the user is anonymous OR the user has access via a guest password)
 * we use the public URL. Otherwise, we include the redirect and org_key params to prompt login to allow editing.
 */
export const generatePermissionSensitiveBrandguideUrl = (options: GenerateBrandguideUrlOptions): string => {
  const { public: publicBrandguide, v2 } = options;
  const userAnonymous = getCurrentUserIsAnonymous(BFG.currentUser);

  if (publicBrandguide && userAnonymous && v2) {
    return generatePublicV2BrandguideUrl(options);
  }

  return generateBrandguideUrl(options);
};
